import React from 'react';
import { Button, Header3Text, PageLoading } from 'ui-toolkit';
import { ICheckDepositData } from 'types/order/escrow/ICheckDeposit';
import { FieldArray, Form, Formik, FormikHelpers } from 'formik';
import { assertDefined } from '../../../../utilities/Assertions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { IncomingCheckTable } from './IncomingCheckTable';
import { StyledFeeSection, StyledFeeSectionHeader } from '../FeeSections.Styled';
import { IncomingCheckDepositValidationSchema } from './IncomingCheckDepositValidationSchema';
import {
  useCreateCheckDepositMutation,
  useDeleteCheckDepositMutation,
  useUpdateCheckDepositMutation,
} from 'API/order/escrow/EscrowBaseAPI';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { StyledIncomingFlexRow } from './StyledIncomingFlexRow';

interface IIncomingCheckCard {
  checkDeposits?: ICheckDepositData[];
  orderId: string;
  escrowFileId?: string;
}

export const IncomingCheckSection: React.FC<IIncomingCheckCard> = ({ checkDeposits, orderId, escrowFileId }) => {
  assertDefined(checkDeposits);
  assertDefined(escrowFileId);

  const [onCreate, { isLoading: isCreating }] = useCreateCheckDepositMutation();
  const [onUpdate, { isLoading: isUpdating }] = useUpdateCheckDepositMutation();
  const [onDelete, { isLoading: isDeleting }] = useDeleteCheckDepositMutation();

  const onSave = async (values: { checkDeposits: ICheckDepositData[] }, { setFieldValue }: FormikHelpers<any>) => {
    values.checkDeposits.forEach(async (deposit, index) => {
      if (!deposit.isEditing) {
        return;
      }

      if (deposit.createdAt) {
        const updateResult = await onUpdate({ ...deposit, orderId, escrowFileId });
        if (checkForMutationSuccess(updateResult, 'updating check')) {
          setFieldValue(`checkDeposits.${index}.isEditing`, false);
        }
      } else {
        const result = await onCreate({ ...deposit, orderId, escrowFileId });
        if (checkForMutationSuccess(result, 'creating check')) {
          setFieldValue(`checkDeposits.${index}.isEditing`, false);
        }
      }
    });
  };

  return (
    <StyledFeeSection>
      <PageLoading isLoading={isCreating || isUpdating || isDeleting} />
      <StyledFeeSectionHeader>
        <Header3Text>Incoming Checks</Header3Text>
      </StyledFeeSectionHeader>
      <Formik
        initialValues={{ checkDeposits }}
        validationSchema={IncomingCheckDepositValidationSchema}
        onSubmit={onSave}
        enableReinitialize
      >
        {({ values, handleSubmit }) => (
          <Form onSubmit={handleSubmit}>
            <FieldArray name={'checkDeposits'}>
              {({ push, remove }) => (
                <>
                  <IncomingCheckTable onDelete={(id) => onDelete({ id, orderId })} remove={remove} />
                  <StyledIncomingFlexRow>
                    <Button
                      intent={'primary'}
                      onClick={() => push({ version: 'v2-fees', isEditing: true })}
                      data-test-id={'add-check-button'}
                    >
                      <FontAwesomeIcon icon={faPlus} /> Add Check
                    </Button>
                    {values.checkDeposits.length > 0 && (
                      <Button intent={'secondary'} type={'submit'} data-test-id={'update-check-button'}>
                        Update Escrow File
                      </Button>
                    )}
                  </StyledIncomingFlexRow>
                </>
              )}
            </FieldArray>
          </Form>
        )}
      </Formik>
    </StyledFeeSection>
  );
};
